// src/services/appointmentService.js
import { supabase } from "../supabaseClient";

/* ------------------------------------------------------------
   HELPERS
------------------------------------------------------------ */
const toNullIfEmpty = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toIntOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : null;
};

const normalizePhone = (v) => String(v || "").replace(/\D/g, "");

export const appointmentService = {
  /* ----------------------------------------------------------
     CREATE (public form)
  ---------------------------------------------------------- */
  async createAppointment(payload) {
    const insertRow = {
      customer_name: String(payload.customer_name || "").trim(),
      phone: normalizePhone(payload.phone),
      whatsapp: toNullIfEmpty(payload.whatsapp ? normalizePhone(payload.whatsapp) : null),
      email: toNullIfEmpty(payload.email),

      appointment_date: toNullIfEmpty(payload.appointment_date),
      appointment_time: toNullIfEmpty(payload.appointment_time),
      appointment_type: toNullIfEmpty(payload.appointment_type),

      vehicle_brand: toNullIfEmpty(payload.vehicle_brand),
      vehicle_model: toNullIfEmpty(payload.vehicle_model),
      vehicle_year: toIntOrNull(payload.vehicle_year),
      expected_price: toNumberOrNull(payload.expected_price),
      registration_number: toNullIfEmpty(
        payload.registration_number ? String(payload.registration_number).toUpperCase() : null
      ),

      message: toNullIfEmpty(payload.message),

      status: "pending",
    };

    if (!insertRow.customer_name) throw new Error("Customer name is required");
    if (!insertRow.phone || insertRow.phone.length < 10) {
      throw new Error("Enter a valid mobile number");
    }
    if (!insertRow.appointment_type) throw new Error("Appointment type is required");

    const { data, error } = await supabase
      .from("appointments")
      .insert([insertRow])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     LIST (admin / public view)
  ---------------------------------------------------------- */
  async listAppointments({ status, search, limit = 200 } = {}) {
    let query = supabase
      .from("appointments")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (status) query = query.eq("status", status);

    if (search && search.trim()) {
      const s = `%${search.trim()}%`;
      query = query.or(
        `customer_name.ilike.${s},phone.ilike.${s},email.ilike.${s},vehicle_brand.ilike.${s},vehicle_model.ilike.${s},registration_number.ilike.${s}`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /* ----------------------------------------------------------
     GET SINGLE
  ---------------------------------------------------------- */
  async getAppointment(id) {
    const { data, error } = await supabase
      .from("appointments")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     UPDATE
  ---------------------------------------------------------- */
  async updateAppointment(id, { status, admin_notes, assigned_to } = {}) {
    const patch = {};
    if (status !== undefined) patch.status = status;
    if (admin_notes !== undefined) patch.admin_notes = admin_notes;
    if (assigned_to !== undefined) patch.assigned_to = assigned_to;

    if (status && ["scheduled", "completed", "cancelled"].includes(status)) {
      patch.handled_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from("appointments")
      .update(patch)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /* ----------------------------------------------------------
     DELETE
  ---------------------------------------------------------- */
  async deleteAppointment(id) {
    const { error } = await supabase
      .from("appointments")
      .delete()
      .eq("id", id);
    if (error) throw error;
  },

  /* ----------------------------------------------------------
     STATS
  ---------------------------------------------------------- */
  async getAppointmentStats() {
    const statuses = ["pending", "contacted", "scheduled", "completed", "cancelled"];

    const counts = await Promise.all(
      statuses.map(async (s) => {
        const { count, error } = await supabase
          .from("appointments")
          .select("id", { count: "exact", head: true })
          .eq("status", s);
        if (error) throw error;
        return { status: s, count: count || 0 };
      })
    );

    const { count: total } = await supabase
      .from("appointments")
      .select("id", { count: "exact", head: true });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { count: todayCount } = await supabase
      .from("appointments")
      .select("id", { count: "exact", head: true })
      .gte("created_at", today.toISOString());

    return {
      total: total || 0,
      today: todayCount || 0,
      byStatus: counts.reduce((acc, { status, count }) => {
        acc[status] = count;
        return acc;
      }, {}),
    };
  },

  /* ----------------------------------------------------------
     NOTIFICATIONS (reuses admin_notifications)
  ---------------------------------------------------------- */
  async listAppointmentNotifications({ unreadOnly = false, limit = 10 } = {}) {
    let query = supabase
      .from("admin_notifications")
      .select("*")
      .eq("type", "appointment")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (unreadOnly) query = query.eq("is_read", false);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async markNotificationRead(id) {
    const { error } = await supabase
      .from("admin_notifications")
      .update({ is_read: true })
      .eq("id", id);
    if (error) throw error;
  },
};